import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'

import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import CreditApplicationPageLayout from '@sputnik/pages/CreditApplicationPages/CreditApplicationPageLayout'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import AddManualBankAccountDrawer from './AddManualBankAccountDrawer'
import AddMXBankAccountDrawer from './AddMXBankAccountDrawer'
import BankAccountConnectionChoice from './BankAccountConnectionChoice'
import ConnectBankAccountFormMarketingContent from './ConnectBankAccountFormMarketingContent'
import ConnectedBankAccounts from './ConnectedBankAccounts'
import { useGetBankAccountInfo } from './GetBankAccountInfo.gql'
import ManualBankAccount from './ManualBankAccount'
import styles from './styles.module.scss'
import { isBankInfoPopulated } from './utils'

const ConnectBankAccountForm = () => {
  const navigate = useNavigate()
  const { appId } = useParams()
  const { formatMessage } = useIntl()
  const [getClassName] = useNameFormatter('ConnectBankAccountForm')
  const { isMajorAccountChange, isMigration } = useGetCreditAppTypes({ salesforceCreditId: appId ?? '' })
  const { track } = useWebTracking()

  const [isShowingAddManualBankAccountDrawer, setIsShowingAddManualBankAccountDrawer] = useState(false)
  const [isShowingAddMXBankAccountDrawer, setIsShowingAddMXBankAccountDrawer] = useState(false)
  // mutation to update the form field sections here.

  const { data } = useGetBankAccountInfo({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  /* tracking start */
  useEffect(() => {
    track('se_signup__application_financial_info_connect_bank__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */
  const hasManualBankAccountConfigured = isBankInfoPopulated(data?.creditApplication?.financeInfo?.bankInfo)

  if (isMajorAccountChange || isMigration) {
    navigate(`../${PATHNAME_CONSTS.REVIEW_PATH}`)
  }

  return (
    <div className={styles[getClassName('')]}>
      <div className={styles[getClassName('form')]}>
        <CreditApplicationPageLayout
          marketing={<ConnectBankAccountFormMarketingContent />}
          pageTitle={
            <FormattedMessage
              defaultMessage='Connect your business bank account'
              id='sputnik.ConnectBankAccountForm__nameAndOwnership'
            />
          }
          onBackClick={() => {
            track('se_signup__application_financial_info_bank_connection_back__submitted')
            navigate(`../${PATHNAME_CONSTS.FINANCIAL_DETAILS_PATH}`)
          }}
          onNextClick={() => {
            track('se_signup__application_financial_info_bank_connection_next__submitted')
            navigate(`../${PATHNAME_CONSTS.PEOPLE_INFO_PATH}`)
          }}
        >
          <div className={styles[getClassName('content')]}>
            <div className={styles[getClassName('choices')]}>
              <BankAccountConnectionChoice
                action={{
                  text: formatMessage({
                    defaultMessage: 'Connect with MX',
                    id: 'sputnik.ConnectBankAccountForm__J3lEj4',
                  }),
                  onClick: () => {
                    track('se_signup__application_financial_info_connect_mx_continue__submitted')
                    setIsShowingAddMXBankAccountDrawer(true)
                  },
                }}
                image={{
                  name: 'connect-bank-account-mx-tile',
                  alt: 'rocket ship',
                }}
                subtitle={formatMessage({
                  defaultMessage: 'Connect as many accounts as you like in one step. All you need is your log in info.',
                  id: 'sputnik.ConnectBankAccountForm__BF/vD9',
                })}
                title={formatMessage({
                  defaultMessage: 'Connect an account automatically',
                  id: 'sputnik.ConnectBankAccountForm__FJQCSl',
                })}
              />
              <div className={styles[getClassName('divider')]}>
                <FormattedMessage
                  defaultMessage='Or'
                  id='sputnik.ConnectBankAccountForm__4wpUNc'
                />
              </div>
              <BankAccountConnectionChoice
                action={{
                  text: formatMessage({
                    defaultMessage: 'Add account manually',
                    id: 'sputnik.ConnectBankAccountForm__UJ7HJx',
                  }),
                  onClick: () => {
                    track('se_signup__application_financial_info_connect_manually__submitted')
                    setIsShowingAddManualBankAccountDrawer(true)
                  },
                  isDisabled: hasManualBankAccountConfigured,
                  disabledTooltipText: formatMessage({
                    defaultMessage: 'Only one account can be added manually',
                    id: 'sputnik.ConnectBankAccountForm__Xb2b/E',
                  }),
                }}
                image={{
                  name: 'connect-bank-account-manual-tile',
                  alt: 'pen',
                }}
                subtitle={formatMessage({
                  defaultMessage:
                    'You’ll need the business name on the account,  bank account number, and routing number.',
                  id: 'sputnik.ConnectBankAccountForm__fEmC1a',
                })}
                title={formatMessage({
                  defaultMessage: 'Add an account manually',
                  id: 'sputnik.ConnectBankAccountForm__QDVjrc',
                })}
              />
            </div>
            <ConnectedBankAccounts />
            <ManualBankAccount />
          </div>
        </CreditApplicationPageLayout>
      </div>
      <AddManualBankAccountDrawer
        isShowing={isShowingAddManualBankAccountDrawer}
        onClose={() => {
          track('se_signup__application_financial_info_connect_manually_add_account__cancelled')
          setIsShowingAddManualBankAccountDrawer(false)
        }}
      />
      <AddMXBankAccountDrawer
        isShowing={isShowingAddMXBankAccountDrawer}
        onClose={() => {
          track('se_signup__application_financial_info_connect_mx__closed')
          setIsShowingAddMXBankAccountDrawer(false)
        }}
      />
    </div>
  )
}

export default ConnectBankAccountForm
